/** @jsx jsx */
import * as React from "react";
import { jsx } from "@emotion/core";
import { connect } from "react-redux";
import ReactPaginate from 'react-paginate';

import { RouteUtils, FITNESS_PLAN_CREATE } from "../../../routes";
import { IAppState } from "../../../models/IAppState";
import { UserFitnessActions } from "../../../actions/UserFitnessActions";
import { IAllFitnessPlansProps, IMapStateToProps, IMapDispatchToProps, IPaginationParams } from "./interface";
import { get } from "lodash";
import * as styles from './style';
import CTA, { CTATheme } from "../../../components/common/CallToAction";
import { SearchBar } from "../../../components/common/SearchBarV1/SearchBar";
import { IPaginatedMicrocycles, formFieldNames, IMicrocycle } from "../FitnessTemplate/interface";
import { FITNESS_GOAL, GENDER } from "../constants";
import { IUserFitnessPlanRequestParams } from "../../Patient/Transform/interface";

const FitnessPlanList: React.FC<IAllFitnessPlansProps> = (props) => {
    const { fetchAllExercises: fetchExercises, params, allExercises, fetchAllFitnessPlans, accountData, assignFitnessPlan } = props;

    const isFirstRender = React.useRef(true)
    const PAGE_LIMIT = 10;
    const INITIAL_PAGINATION_PARAM: IPaginationParams = { offset: 0, limit: PAGE_LIMIT };
    const loggedInUserParam = { createdBy: { value: accountData.id, query: "eq" } };
    const [allFitnessPlans, setAllPlans] = React.useState<IPaginatedMicrocycles>(null);
    const [searchParams, setSearchParams] = React.useState(loggedInUserParam);
    const [paginationParams, setPaginationParams] = React.useState(INITIAL_PAGINATION_PARAM);
    const [currentPage, setCurrentPage] = React.useState(0);

    React.useEffect(() => {
        if (!isFirstRender.current) {
            fetchPlans();
        }
    }, [paginationParams, searchParams]);

    React.useEffect(() => {
        isFirstRender.current = false // toggle flag after first render/mounting
        //this is done so that page loads quickly while creating a template form
        if (!allExercises) {
            fetchExercises();
        }
        fetchPlans();
    }, []);

    const fetchPlans = () => {
        //add sort params
        let params = { ...paginationParams, sortBy: "createdOn", sortOrder: "DESC" };
        fetchAllFitnessPlans(searchParams, params).then((plans: IPaginatedMicrocycles) => {
            setAllPlans(plans);
        })
    }

    const createFitnessPlan = () => {
        RouteUtils.navigateToHrefUrl(null, FITNESS_PLAN_CREATE)
    }

    const editFitnessPlan = (planId: any) => {
        RouteUtils.navigateToHrefUrl(null, RouteUtils.getEditFitnessPlanUrl(planId));
    }

    const filterPlans = (value: string, fieldName: string) => {
        value ? setSearchParams({ ...loggedInUserParam, [fieldName]: { value, query: "like" } }) : setSearchParams(loggedInUserParam);
        setPaginationParams(INITIAL_PAGINATION_PARAM)
    }

    const handlePageClick = (selectedItem: any) => {
        setCurrentPage(selectedItem.selected);
        const offset = selectedItem.selected * PAGE_LIMIT;
        setPaginationParams({ offset, limit: PAGE_LIMIT });
    }

    const duplicatePlan = (selectedPlan: IMicrocycle) => {
        const reqParams: IUserFitnessPlanRequestParams = {
            createdBy: accountData.id,
            description: `${selectedPlan.description}-duplicate`
        }

        assignFitnessPlan(reqParams, selectedPlan.id).then((response: any) => {
            fetchPlans();
        })
    }

    const viewPlan = (planId: number) => {
        RouteUtils.navigateToHrefUrl(null, RouteUtils.getViewFitnessPlanUrl(planId.toString()));
    }

    const pageCount = allFitnessPlans && Math.ceil(allFitnessPlans.totalElements / PAGE_LIMIT);
    return (
        <div css={styles.outerWrapper}>
            <div css={styles.topBar}>
                <SearchBar placeholder="Search by Template Name" fieldName={formFieldNames.TEMPLATENAME} handleSubmit={filterPlans} />
                <CTA innerText="Create New Template" theme={CTATheme.PRIMARY} onClick={createFitnessPlan} customStyle={styles.actionButton} />
            </div>
            <div css={styles.plansList}>
                {
                    (allFitnessPlans && allFitnessPlans.elements.length > 0) ? <React.Fragment>
                        <table css={styles.tableWrapper}>
                            <thead>
                                <tr css={styles.tableHeader}>
                                    <th css={styles.firstCol}>Template Name</th>
                                    {/* <td>Format</td> */}
                                    <th css={styles.secondCol}>Goal Focus</th>
                                    <th css={styles.thirdCol}>Gender</th>
                                    <th css={styles.fourthCol}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {allFitnessPlans.elements.map(plan => {
                                    const goalFocus = FITNESS_GOAL.find(({ internalValue }) => internalValue === plan.goalId);
                                    const gender = GENDER.find(({ internalValue }) => internalValue === plan.meta.gender);

                                    return (
                                        <tr css={styles.tableData} key={plan.id} onClick={() => viewPlan(plan.id)}>
                                            <td css={[styles.firstCol, styles.dataRow]}>{plan.description}</td>
                                            <td css={[styles.secondCol, styles.dataRow]}>{goalFocus ? goalFocus.displayValue : "-"}</td>
                                            <td css={[styles.thirdCol, styles.dataRow]}>{gender ? gender.displayValue : "-"}</td>
                                            <td css={[styles.fourthCol, styles.dataRow]} onClick={(e) => e.stopPropagation()} >
                                                <span css={styles.subColumn} onClick={() => editFitnessPlan(plan.id)}>Edit</span>
                                                <span css={styles.subColumn} onClick={() => duplicatePlan(plan)}>Duplicate</span>
                                            </td>
                                        </tr>
                                    )
                                })
                                }
                            </tbody>
                        </table>
                        <div className="fitness-pagination">
                            <ReactPaginate
                                previousLabel={<React.Fragment>< img src="/images/back-arrow.svg" className="pagination__icon previous" /></React.Fragment>}
                                nextLabel={<React.Fragment><img src="/images/back-arrow.svg" className="pagination__icon next" /></React.Fragment>}
                                pageCount={pageCount}
                                onPageChange={handlePageClick}
                                containerClassName={"pagination"}
                                previousLinkClassName={"pagination__link"}
                                nextLinkClassName={"pagination__link"}
                                disabledClassName={"pagination__link--disabled"}
                                activeClassName={"pagination__link--active"}
                                pageRangeDisplayed={3}
                                marginPagesDisplayed={3}
                                initialPage={0}
                                pageClassName={"pagination__item"}
                            />
                        </div>
                    </React.Fragment> : <div css={styles.notFound}>No Templates Found!</div>
                }
            </div>

        </div >
    )
}

const mapStateToProps = (state: IAppState): IMapStateToProps => ({
    allExercises: get(state, "fitnessData.exercises", null),
    accountData: state.accountData
});

const mapDispatchToProps = (dispatch: React.Dispatch<any>): IMapDispatchToProps => ({
    fetchAllExercises: () => UserFitnessActions.fetchAllExercises(dispatch),
    fetchAllFitnessPlans: (params: { [key: string]: { value: string | number, query: string } }, paginationParams: IPaginationParams) => UserFitnessActions.fetchAllFitnessPlans(params, paginationParams),
    assignFitnessPlan: (requestParams: IUserFitnessPlanRequestParams, selectedPlanId: number) => UserFitnessActions.assignFitnessPlan(requestParams, selectedPlanId)
})

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FitnessPlanList);